import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Check, Clock, Timer, Pencil, Trash2, Droplet } from "lucide-react";
import { nextDoseAt, humanRelative, fmtLocalDT } from "@/lib/clinical";
import { useAuth } from "@/lib/auth";

const QUICK_SETS = {
  "RDS (Surfactant + support)": [
    { name: "Surfactant (Poractant alfa)", dose: "200 mg/kg", route: "ET", frequency: "stat" },
    { name: "Caffeine citrate", dose: "20 mg/kg load, 5 mg/kg OD", route: "IV/PO", frequency: "OD" },
  ],
  "Neonatal Sepsis (empiric)": [
    { name: "Ampicillin", dose: "50 mg/kg", route: "IV", frequency: "q12h" },
    { name: "Gentamicin", dose: "4 mg/kg", route: "IV", frequency: "OD" },
  ],
  "Hypoglycemia protocol": [
    { name: "10% Dextrose bolus", dose: "2 ml/kg", route: "IV", frequency: "stat" },
  ],
  "Inotropes & infusions": [
    { name: "Dopamine", dose: "5-20 mcg/kg/min", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Dobutamine", dose: "5-20 mcg/kg/min", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Adrenaline (Epinephrine)", dose: "0.05-1 mcg/kg/min", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Noradrenaline", dose: "0.05-1 mcg/kg/min", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Milrinone", dose: "0.25-0.75 mcg/kg/min", route: "IV", frequency: "infusion", category: "infusion" },
  ],
  "Sedation / analgesia infusions": [
    { name: "Fentanyl", dose: "1-4 mcg/kg/hr", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Midazolam", dose: "0.05-0.15 mg/kg/hr", route: "IV", frequency: "infusion", category: "infusion" },
    { name: "Morphine", dose: "10-40 mcg/kg/hr", route: "IV", frequency: "infusion", category: "infusion" },
  ],
};

export default function MedicationsTab({ patient }) {
  const { user } = useAuth();
  const canEdit = ["doctor", "admin"].includes(user?.role);
  const [orders, setOrders] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [f, setF] = useState({ name: "", dose: "", route: "IV", frequency: "q8h", category: "regular", notes: "", dilution: "", rate_ml_hr: "" });
  const [view, setView] = useState("regular");  // "regular" | "infusion"
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const [{ data: o }, { data: a }] = await Promise.all([
      api.get(`/medications/orders/${patient.id}`),
      api.get(`/medications/administrations/${patient.id}`),
    ]);
    setOrders(o); setAdmins(a);
  }, [patient.id]);
  useEffect(() => { load(); }, [load]);

  // Tick every minute so the "next dose in …" label stays fresh.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const addOrder = async () => {
    if (!f.name || !f.dose) { toast.error("Name & dose required"); return; }
    try {
      const { data } = await api.post("/medications/orders", { patient_id: patient.id, ...f });
      if (data.duplicate_warning) toast.warning("Duplicate drug — please review");
      (data.interaction_warnings || []).forEach((w) => {
        const icon = w.severity === "major" ? "error" : "warning";
        toast[icon](`Interaction with ${w.with}: ${w.text}`, { duration: 8000 });
      });
      setF({ name: "", dose: "", route: "IV", frequency: "q8h", category: "regular", notes: "", dilution: "", rate_ml_hr: "" });
      load();
    } catch { toast.error("Failed to add order"); }
  };

  const applyQuickSet = async (name) => {
    for (const drug of QUICK_SETS[name]) {
      try { await api.post("/medications/orders", { patient_id: patient.id, ...drug }); }
      catch (e) { console.warn("quick-set add failed", drug.name, e); }
    }
    toast.success(`${name} added`);
    load();
  };

  const give = async (orderId, dose) => {
    try {
      await api.post("/medications/administrations", { order_id: orderId, dose_given: dose });
      toast.success("Dose recorded");
      load();
    } catch { toast.error("Failed"); }
  };

  const deactivate = async (id) => {
    await api.patch(`/medications/orders/${id}?active=false`);
    load();
  };

  const removeOrder = async (id) => {
    try {
      await api.delete(`/medications/orders/${id}`);
      toast.success("Medication removed");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const saveEdit = async (updates) => {
    try {
      await api.patch(`/medications/orders/${editing.id}`, updates);
      toast.success("Medication updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const lastGiven = (orderId) => admins.find(a => a.order_id === orderId);

  const filteredOrders = orders.filter((o) => (o.category || "regular") === view);

  return (
    <div className="space-y-6" data-testid="medications-tab">
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setView("regular"); setF((s) => ({ ...s, category: "regular", frequency: s.frequency === "infusion" ? "q8h" : s.frequency })); }}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${view === "regular" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
          data-testid="med-view-regular"
        >Regular meds</button>
        <button
          onClick={() => { setView("infusion"); setF((s) => ({ ...s, category: "infusion", frequency: "infusion" })); }}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${view === "infusion" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
          data-testid="med-view-infusion"
        >Infusions / Inotropes</button>
      </div>

      <div className="card-clinical p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
          Add {view === "infusion" ? "infusion" : "order"}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input placeholder="Drug name" value={f.name} onChange={(e) => setF(s => ({ ...s, name: e.target.value }))} data-testid="med-name" />
          <Input placeholder={view === "infusion" ? "Dose (e.g. 5-20 mcg/kg/min)" : "Dose"} value={f.dose} onChange={(e) => setF(s => ({ ...s, dose: e.target.value }))} data-testid="med-dose" />
          <Select value={f.route} onValueChange={(v) => setF(s => ({ ...s, route: v }))}>
            <SelectTrigger data-testid="med-route"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["IV", "IM", "PO", "SC", "ET", "PR", "Topical"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.frequency} onValueChange={(v) => setF(s => ({ ...s, frequency: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {(view === "infusion"
                  ? ["infusion", "stat"]
                  : ["stat", "OD", "BD", "TDS", "QID", "q4h", "q6h", "q8h", "q12h"]
              ).map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={addOrder} data-testid="med-add" className="rounded-full">Add</Button>
        </div>
        {view === "infusion" && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="med-infusion-extra">
            <div>
              <label className="text-[11px] text-muted-foreground flex items-center gap-1"><Droplet size={11}/> Dilution / preparation</label>
              <Input placeholder="e.g. Dopamine 40mg in 40ml NS (1mg/ml) via central line"
                     value={f.dilution} onChange={(e) => setF(s => ({ ...s, dilution: e.target.value }))}
                     data-testid="med-dilution" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground flex items-center gap-1"><Timer size={11}/> Rate (ml/hr)</label>
              <Input placeholder="e.g. 3.2 ml/hr" value={f.rate_ml_hr}
                     onChange={(e) => setF(s => ({ ...s, rate_ml_hr: e.target.value }))}
                     data-testid="med-rate-ml-hr" />
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground mr-1">Quick sets:</span>
          {Object.keys(QUICK_SETS).map(k => (
            <button key={k} onClick={() => applyQuickSet(k)} className="text-xs px-3 py-1 rounded-full border border-border hover:border-primary hover:text-primary">
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="card-clinical">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3">Drug</div>
          <div className="col-span-2">Dose {view === "infusion" ? "(rate)" : ""}</div>
          <div className="col-span-1">Route</div>
          <div className="col-span-1">Freq</div>
          <div className="col-span-2">{view === "infusion" ? "Started" : "Last given"}</div>
          <div className="col-span-2">{view === "infusion" ? "Running" : "Next dose"}</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {filteredOrders.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground text-center">
            No {view === "infusion" ? "infusions" : "orders"} yet
          </div>
        )}
        {filteredOrders.map((o) => {
          const lg = lastGiven(o.id);
          const nd = nextDoseAt(o, lg);
          const overdue = !lg && o.active;
          return (
            <div key={o.id} className="grid grid-cols-12 px-4 py-3 border-b border-border/60 items-center text-sm" data-tick={tick}>
              <div className="col-span-3">
                <div className="font-medium">{o.name}</div>
                {o.duplicate_warning && <div className="text-[10px] status-warn flex items-center gap-1"><AlertTriangle size={11} /> duplicate</div>}
                {(o.interaction_warnings || []).map((w, i) => (
                  <div key={`${o.id}-int-${w.with}-${i}`} className={`text-[10px] flex items-start gap-1 mt-0.5 ${w.severity === "major" ? "status-critical" : "status-warn"}`} title={w.text}>
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    <span className="truncate">↔ {w.with} ({w.severity})</span>
                  </div>
                ))}
                {!o.active && <div className="text-[10px] text-muted-foreground">stopped</div>}
                {view === "infusion" && (o.dilution || o.rate_ml_hr) && (
                  <div className="mt-1 text-[10px] text-muted-foreground space-y-0.5">
                    {o.dilution && <div className="flex items-start gap-1"><Droplet size={9} className="mt-0.5 shrink-0"/> <span className="truncate" title={o.dilution}>{o.dilution}</span></div>}
                    {o.rate_ml_hr && <div className="flex items-center gap-1"><Timer size={9}/> {o.rate_ml_hr} ml/hr</div>}
                  </div>
                )}
              </div>
              <div className="col-span-2 tabular">{o.dose}</div>
              <div className="col-span-1">{o.route}</div>
              <div className="col-span-1">{o.frequency}</div>
              <div className="col-span-2 tabular text-xs">
                {lg ? <span className="inline-flex items-center gap-1"><Check size={12} className="status-vacant" /> {fmtLocalDT(lg.given_at)}</span>
                    : <span className={`inline-flex items-center gap-1 ${overdue ? "status-critical" : "text-muted-foreground"}`}><Clock size={12} /> not given yet</span>}
              </div>
              <div className="col-span-2 tabular text-xs" data-testid={`med-next-${o.id}`}>
                {!o.active ? (
                  <span className="text-muted-foreground">—</span>
                ) : !nd ? (
                  <span className="text-muted-foreground italic">stat / prn</span>
                ) : (
                  <span className={`inline-flex items-center gap-1 ${nd.overdue ? "status-critical" : nd.hoursLeft < 1 ? "status-warn" : ""}`}
                        title={nd.at.toLocaleString()}>
                    <Timer size={12} /> {nd.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    <span className={`text-[10px] ${nd.overdue ? "" : "text-muted-foreground"}`}>({humanRelative(nd.hoursLeft)})</span>
                  </span>
                )}
              </div>
              <div className="col-span-1 text-right flex justify-end gap-1">
                {o.active && <Button size="sm" variant="secondary" className="rounded-full h-7 text-xs" onClick={() => give(o.id, o.dose)} data-testid={`med-give-${o.id}`}>Give</Button>}
                {o.active && <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => deactivate(o.id)}>Stop</Button>}
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-primary"
                            title="Edit" onClick={() => setEditing(o)} data-testid={`med-edit-${o.id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-destructive"
                            title="Delete duplicate / wrong entry" onClick={() => setDeleting(o)} data-testid={`med-delete-${o.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && <EditMedModal order={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}
      {deleting && (
        <Dialog open onOpenChange={() => setDeleting(null)}>
          <DialogContent data-testid="med-delete-modal">
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-2"><Trash2 size={16} /> Delete medication?</DialogTitle>
              <DialogDescription>
                Permanently remove <b>{deleting.name}</b> ({deleting.dose} · {deleting.route} · {deleting.frequency}) and all its administration records.
                Use this only for duplicates or wrong entries.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => removeOrder(deleting.id)} data-testid="med-delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditMedModal({ order, onClose, onSave }) {
  const isInfusion = (order.category || "regular") === "infusion";
  const [f, setF] = useState({
    name: order.name || "",
    dose: order.dose || "",
    route: order.route || "IV",
    frequency: order.frequency || (isInfusion ? "infusion" : "q8h"),
    notes: order.notes || "",
    dilution: order.dilution || "",
    rate_ml_hr: order.rate_ml_hr || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="med-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={16}/> Edit medication</DialogTitle>
          <DialogDescription>Fix typing mistakes or update dose / rate. All edits are audit-logged.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[11px] text-muted-foreground">Drug name</label>
            <Input value={f.name} onChange={(e) => setF(s => ({ ...s, name: e.target.value }))} data-testid="med-edit-name" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Dose {isInfusion ? "(rate)" : ""}</label>
            <Input value={f.dose} onChange={(e) => setF(s => ({ ...s, dose: e.target.value }))} data-testid="med-edit-dose" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Route</label>
            <Select value={f.route} onValueChange={(v) => setF(s => ({ ...s, route: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["IV","IM","PO","SC","ET","PR","Topical"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Frequency</label>
            <Select value={f.frequency} onValueChange={(v) => setF(s => ({ ...s, frequency: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(isInfusion ? ["infusion","stat"] : ["stat","OD","BD","TDS","QID","q4h","q6h","q8h","q12h"])
                  .map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isInfusion && (
            <>
              <div className="col-span-2">
                <label className="text-[11px] text-muted-foreground flex items-center gap-1"><Droplet size={11}/> Dilution / preparation</label>
                <Textarea rows={2} value={f.dilution} onChange={(e) => setF(s => ({ ...s, dilution: e.target.value }))}
                          placeholder="e.g. Dopamine 40mg in 40ml NS (1mg/ml) via central line" data-testid="med-edit-dilution" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground flex items-center gap-1"><Timer size={11}/> Rate (ml/hr)</label>
                <Input value={f.rate_ml_hr} onChange={(e) => setF(s => ({ ...s, rate_ml_hr: e.target.value }))} data-testid="med-edit-rate" />
              </div>
              <div></div>
            </>
          )}
          <div className="col-span-2">
            <label className="text-[11px] text-muted-foreground">Notes</label>
            <Textarea rows={2} value={f.notes} onChange={(e) => setF(s => ({ ...s, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="med-edit-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
